import React, { useEffect, useState } from 'react';
import RealSocialFeed from '@/components/listening/RealSocialFeed';
import BrandMonitoringDashboard from '@/components/listening/BrandMonitoringDashboard';
import TrendAnalytics from '@/components/listening/TrendAnalytics';
import { ConnectedPlatformsStatus } from '@/components/common/ConnectedPlatformsStatus';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, BarChart3, Bell, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function SocialListening() {
  const { user } = useAuth();
  const [listeningResults, setListeningResults] = useState([]);

  // Set up real-time updates for social listening
  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('social-listening-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_listening_results',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New social listening result:', payload)
          toast.info('New mention detected!')
          // Add new result to state
          setListeningResults(prev => [payload.new, ...prev])
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_accounts',
          filter: `user_id=eq.${user.id} and platform=eq.facebook`
        },
        (payload) => {
          console.log('Facebook account update in social listening:', payload)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Social Listening</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Monitor brand mentions, track trends, and analyze sentiment across social platforms
          </p>
        </div>
      </div>

      {/* Connected Platforms Status */}
      <ConnectedPlatformsStatus compact={true} />

      {/* Main Content */}
      <div className="w-full overflow-hidden">
        <Tabs defaultValue="feed" className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 max-w-full h-auto p-1">
            <TabsTrigger value="feed" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-2">
              <Search className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Live Feed</span>
              <span className="sm:hidden">Feed</span>
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-2">
              <Bell className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Brand Monitoring</span>
              <span className="sm:hidden">Monitor</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-2">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-2">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Trends</span>
              <span className="sm:hidden">Trends</span>
            </TabsTrigger>
          </TabsList>

        <div className="w-full overflow-x-auto">
          <TabsContent value="feed" className="mt-6">
            <RealSocialFeed />
          </TabsContent>

          <TabsContent value="monitoring" className="mt-6">
            <BrandMonitoringDashboard results={listeningResults} />
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Analytics Dashboard</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Analytics Coming Soon</h3>
                  <p className="text-gray-600">
                    Advanced analytics and reporting features will be available here.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trends" className="mt-6">
            <TrendAnalytics />
          </TabsContent>
        </div>
        </Tabs>
      </div>
    </div>
  );
}

export default SocialListening;